import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import bkp from 'lib/Breakpoints';
import Ad from 'components/Ad';
import WaffleCard from './WaffleCard';

const block = 'waffleList';

const createSections = (items) => {
  const isMobile = bkp.isS();
  // length of top layout is different on mobile
  const firstLayoutLen = isMobile ? 5 : 3;

  // slices elements of non ad type
  const sliceNonAds = (entries, start, end) => {
    const result = [];
    for (let i = start; i < end; i += 1) {
      const { type } = entries[i];
      if (type !== 'ad') result.push(entries[i]);
    }
    return result;
  };
  const subSections = [
    {
      type: 'article',
      items: sliceNonAds(items, 0, firstLayoutLen),
    },
  ];

  let currItems = [];
  const insertArticles = () => {
    if (currItems.length) {
      subSections.push({ type: 'article', items: currItems });
      currItems = [];
    }
  };

  // starts creating layouts after first layout
  // because first layout has different rules than rest
  for (let i = firstLayoutLen; i < items.length; i += 1) {
    const item = items[i];
    const { type, metadata } = item;
    const formattedAd = {
      ...item,
    };

    if (type === 'ad') {
      const { slot } = metadata;
      formattedAd.type = slot;

      // if we're on mobile we insert boxinlines
      // in between articles
      if (slot === 'boxinline' && isMobile) {
        insertArticles();
        subSections.push(formattedAd);

      // if we're not on mobile we insert
      // other types of ad between articles
      } else if (slot !== 'boxinline' && !isMobile) {
        insertArticles();
        subSections.push(formattedAd);
      }
    } else {
      currItems.push(item);
    }
  }
  // insert any leftover articles into layout list
  insertArticles();

  return subSections;
};

const mapSections = ({
  sections = [], cardRenderer, adRenderer, adsIncluded,
}) => {
  const markup = [];
  let currMarkup = [];
  const isMobile = bkp.isS();
  // on desktop, ads switch from left to right every n articles
  let directionSwitch = true;

  let cardIndex = -1;
  const updateCardIndex = (card, ...args) => {
    cardIndex += 1;
    return cardRenderer(card, cardIndex, ...args);
  };

  for (let idx = 0; idx < sections.length; idx += 1) {
    const section = sections[idx];
    const nextSections = sections[idx + 1];
    const key = ((section.items.length && section.items[0].id) || section.id) + idx;

    const firstMarkup = !idx && (
      <div className="layout-grid-container df w-100" key={key}>
        <ul className="layout-grid-item df flex-wrap justify-between align-top gutter-collapse-m">
          {section.items.map((article) => updateCardIndex(article, false))}
        </ul>
      </div>
    );

    const articleLayout = section.type === 'article' && !firstMarkup && (
      <ul
        className={cx(
          'layout-grid-item df flex-wrap justify-between gutter-collapse-m',
          'waffle-list__article-layout',
          { 'waffle-list__space-last waffle-list__ads-included': adsIncluded && section.items.length % 3 === 2 },
        )}
        key={key}
      >
        {section.items.map((s) => updateCardIndex(s, true))}
      </ul>
    );

    const boxinline = section.type === 'boxinline' && (
      <div className={cx('layout-grid-item')} key={key}>
        {adRenderer(section.metadata)}
      </div>
    );
    const midresponsive = section.type === 'midresponsive' && (
      <div
        className={
          cx(
            'layout-grid-item midresponsive',
            'waffle-card__mid-ad',
          )
        }
        key={key}
      >
        {adRenderer(section.metadata)}
      </div>
    );

    const boxflexContainerClasses = 'layout-grid-item grid-col-4-m boxflexAd';

    // boxflex Ads render as standard cards
    const boxflexAd = section.type === 'boxflex' && (
      <ul className={boxflexContainerClasses} key={key}>
        {cardRenderer(section)}
      </ul>
    );

    const skippedAd = (<ul className={cx(boxflexContainerClasses, 'skippedAd')} key={key} />);

    if (firstMarkup) {
      markup.push(firstMarkup);
    } else if (articleLayout) {
      currMarkup.push(articleLayout);
    } else if (boxinline) {
      markup.push(boxinline);
    } else if (midresponsive) {
      const midresponsivePlusLayout = (
        <div
          className={
            cx(
              'df flex-column flex-column-reverse-l justify-center w-100',
              'waffle-card__mid-responsive-mobile',
            )
          }
          key={key}
        >
          {markup.pop()}
          {midresponsive}
        </div>
      );
      markup.push(midresponsivePlusLayout);
    } else if (boxflexAd) {
      if (nextSections && nextSections.items && nextSections.items.length <= 2) {
        currMarkup.push(skippedAd);
      } else {
        currMarkup.push(boxflexAd);
      }
    }

    if (isMobile && currMarkup.length) {
      markup.push(...currMarkup);
      currMarkup = [];
    } else if (!isMobile && currMarkup.length === 2) {
      const [ad, article] = currMarkup;
      const sectionMarkup = (
        <div
          className={cx('layout-grid-container df w-100', 'waffle-list__container')}
          key={key}
        >
          {!directionSwitch ? (
            <>
              {article}
              {ad}
            </>
          ) : (
            <>
              {ad}
              {article}
            </>
          )}
        </div>
      );
      directionSwitch = !directionSwitch;
      markup.push(sectionMarkup);

      currMarkup = [];
    }
  }

  return markup;
};

function WaffleList({
  cards, cardRenderer, adsDisabled, ...rest
}) {
  const numberOfArticles = cards.filter((card) => card && card.type !== 'ad').length;

  const defaultCardRenderer = cardRenderer
    || ((card, index, inAdLayout) => (
      <WaffleCard
        key={card.id}
        index={index}
        cardsLength={numberOfArticles}
        card={card}
        inAdLayout={inAdLayout !== false}
        {...rest}
      />
    ));

  const adRenderer = (metadata) => (
    <Ad
      packageId={metadata.packageId}
      slot={metadata.slot}
      offsetViewport={metadata.slot === 'boxinline' ? 50 : null}
      adClass={
        cx(
          'df justify-center',
          'waffle-card__ad',
        )
      }
    />
  );

  const adsIncluded = cards.some((card) => card && card.type === 'ad');
  const sections = !adsDisabled && adsIncluded
    ? createSections(cards, 'ad')
    : [{ type: 'article', items: cards }];

  return (
    <div
      className={cx(
        block,
        'align-top',
        { spaceLast: (!adsIncluded || adsDisabled) && cards.length % 3 === 2 },
      )}
    >
      {mapSections({
        sections, cardRenderer: defaultCardRenderer, adRenderer, adsIncluded,
      })}
    </div>
  );
}

WaffleList.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  cardRenderer: PropTypes.func,
  metadata: PropTypes.objectOf(PropTypes.any),
  adsDisabled: PropTypes.bool,
};

WaffleList.defaultProps = {
  cards: [],
  cardRenderer: null,
  metadata: {},
  adsDisabled: false,
};

export default WaffleList;
